import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Calendar,
  Clock,
  Users,
  Plus,
  X,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  BarChart3,
  Zap,
  Target,
  Activity,
  Award,
  MapPin,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  LayoutList,
  CalendarClock,
  Settings2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { getErrorMessage } from '../utils/errorHandler';
import { format, addDays, startOfWeek, endOfWeek, isWithinInterval, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const PlanningPro = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, gantt, kanban, calendar
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);
  
  const [kpiData, setKpiData] = useState({
    totalAgents: 0,
    activeShifts: 0,
    siteCoverage: 0,
    avgOccupancy: 0,
    hoursThisWeek: 0,
    conflictsCount: 0
  });

  const [newShift, setNewShift] = useState({
    agent_id: '',
    site_id: '',
    shift_date: '',
    start_time: '08:00',
    end_time: '16:00',
    shift_type: 'regular',
    status: 'scheduled'
  });

  const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || '';

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const shiftTypeColors = {
    regular: { bg: 'bg-blue-100', border: 'border-blue-600', text: 'text-blue-900', label: 'Régulier' },
    overtime: { bg: 'bg-orange-100', border: 'border-orange-600', text: 'text-orange-900', label: 'Heures Sup' },
    training: { bg: 'bg-purple-100', border: 'border-purple-600', text: 'text-purple-900', label: 'Formation' },
    on_call: { bg: 'bg-green-100', border: 'border-green-600', text: 'text-green-900', label: 'Astreinte' }
  };

  const statusColors = {
    scheduled: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Planifié' },
    in_progress: { bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'En cours' },
    completed: { bg: 'bg-green-50', text: 'text-green-700', label: 'Terminé' },
    cancelled: { bg: 'bg-red-50', text: 'text-red-700', label: 'Annulé' }
  };

  useEffect(() => {
    loadData();
  }, [currentDate]);

  useEffect(() => {
    calculateKPIs();
  }, [shifts, agents, sites, conflicts]);

  const loadData = async () => {
    try {
      setLoading(true);
      const startDate = getStartDate();
      const endDate = getEndDate();
      
      const [shiftsRes, agentsRes, sitesRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/api/planning-advanced/shifts?start_date=${startDate}&end_date=${endDate}`, getAuthHeader()),
        axios.get(`${API_URL}/api/agents`, getAuthHeader()),
        axios.get(`${API_URL}/api/sites`, getAuthHeader()),
        axios.get(`${API_URL}/api/planning-advanced/templates/shifts`, getAuthHeader())
      ]);

      setShifts(shiftsRes.data.shifts || []);
      setAgents(agentsRes.data || []);
      setSites(sitesRes.data || []);
      setShiftTemplates(templatesRes.data.templates || []);
      
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de charger les données"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateKPIs = () => {
    const activeShifts = shifts.filter(s => s.status === 'scheduled' || s.status === 'in_progress');
    const uniqueSites = new Set(shifts.map(s => s.site_id));
    const totalHours = shifts.reduce((acc, s) => acc + (s.duration_hours || 0), 0);
    
    // Calcul du taux d'occupation moyen
    const agentsWithShifts = new Set(shifts.map(s => s.agent_id));
    const occupancy = agents.length > 0 ? (agentsWithShifts.size / agents.length) * 100 : 0;

    setKpiData({
      totalAgents: agents.length,
      activeShifts: activeShifts.length,
      siteCoverage: uniqueSites.size,
      avgOccupancy: occupancy.toFixed(1),
      hoursThisWeek: totalHours,
      conflictsCount: conflicts.length
    });
  };

  const getStartDate = () => {
    const date = startOfWeek(currentDate, { weekStartsOn: 1 });
    return format(date, 'yyyy-MM-dd');
  };

  const getEndDate = () => {
    const date = endOfWeek(currentDate, { weekStartsOn: 1 });
    return format(date, 'yyyy-MM-dd');
  };

  const createShift = async () => {
    if (!newShift.agent_id || !newShift.site_id || !newShift.shift_date) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs requis",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const agent = agents.find(a => a.id === newShift.agent_id);
      const site = sites.find(s => s.id === newShift.site_id);
      
      const start = new Date(`2000-01-01T${newShift.start_time}`);
      const end = new Date(`2000-01-01T${newShift.end_time}`);
      const duration = (end - start) / (1000 * 60 * 60);
      
      const shiftData = {
        ...newShift,
        agent_name: agent?.name || '',
        site_name: site?.name || '',
        duration_hours: duration,
        actual_work_hours: duration - (30 / 60),
      };

      await axios.post(`${API_URL}/api/planning-advanced/shifts`, shiftData, getAuthHeader());

      toast({
        title: "✅ Plage créée",
        description: "La plage de travail a été créée avec succès",
      });

      setShowShiftModal(false);
      resetNewShift();
      loadData();
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de créer la plage"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetNewShift = () => {
    setNewShift({
      agent_id: '',
      site_id: '',
      shift_date: '',
      start_time: '08:00',
      end_time: '16:00',
      shift_type: 'regular',
      status: 'scheduled'
    });
  };

  const deleteShift = async (shiftId) => {
    if (!window.confirm('Supprimer cette plage ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/planning-advanced/shifts/${shiftId}`, getAuthHeader());
      
      toast({
        title: "✅ Supprimé",
        description: "La plage a été supprimée",
      });
      
      loadData();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la plage",
        variant: "destructive",
      });
    }
  };

  const detectConflicts = async () => {
    setLoading(true);
    try {
      const startDate = getStartDate();
      const endDate = getEndDate();
      
      const response = await axios.post(
        `${API_URL}/api/planning-advanced/conflicts/detect`,
        null,
        {
          ...getAuthHeader(),
          params: { start_date: startDate, end_date: endDate }
        }
      );

      setConflicts(response.data.conflicts || []);
      
      toast({
        title: response.data.total_conflicts > 0 ? "⚠️ Conflits détectés" : "✅ Aucun conflit",
        description: `${response.data.total_conflicts} conflit(s) détecté(s)`,
        variant: response.data.total_conflicts > 0 ? "destructive" : "default",
      });
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de détecter les conflits"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = addDays(currentDate, direction * 7);
    setCurrentDate(newDate);
  };

  const getWeekDays = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  const getShiftsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts.filter(s => s.shift_date === dateStr);
  };

  // Vue Dashboard avec KPIs
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card className="border-l-4 border-l-blue-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Agents Total</p>
                <p className="text-2xl font-bold text-blue-900">{kpiData.totalAgents}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plages Actives</p>
                <p className="text-2xl font-bold text-green-900">{kpiData.activeShifts}</p>
              </div>
              <Activity className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Sites Couverts</p>
                <p className="text-2xl font-bold text-purple-900">{kpiData.siteCoverage}</p>
              </div>
              <MapPin className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Taux Occupation</p>
                <p className="text-2xl font-bold text-orange-900">{kpiData.avgOccupancy}%</p>
              </div>
              <Target className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-indigo-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Heures Semaine</p>
                <p className="text-2xl font-bold text-indigo-900">{kpiData.hoursThisWeek}h</p>
              </div>
              <Clock className="w-8 h-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-600">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conflits</p>
                <p className="text-2xl font-bold text-red-900">{kpiData.conflictsCount}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vue Rapide des Plages */}
      <Card>
        <CardHeader>
          <CardTitle>📊 Vue d'ensemble - Cette semaine</CardTitle>
          <CardDescription>
            Du {format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'dd MMM', { locale: fr })} au {format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'dd MMM yyyy', { locale: fr })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2">
            {getWeekDays().map((day, index) => (
              <div key={index} className="border rounded-lg p-2">
                <div className="text-center mb-2">
                  <div className="text-xs text-gray-500">
                    {format(day, 'EEE', { locale: fr })}
                  </div>
                  <div className="text-lg font-bold">
                    {format(day, 'd')}
                  </div>
                </div>
                <div className="space-y-1">
                  {getShiftsForDate(day).slice(0, 3).map((shift, idx) => {
                    const color = shiftTypeColors[shift.shift_type] || shiftTypeColors.regular;
                    return (
                      <div
                        key={idx}
                        className={`text-xs p-1 rounded ${color.bg} ${color.text} truncate`}
                        title={`${shift.agent_name} - ${shift.site_name}`}
                      >
                        {shift.agent_name?.split(' ')[0]}
                      </div>
                    );
                  })}
                  {getShiftsForDate(day).length > 3 && (
                    <div className="text-xs text-gray-500 text-center">
                      +{getShiftsForDate(day).length - 3}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Alertes et Conflits */}
      {conflicts.length > 0 && (
        <Card className="border-l-4 border-l-red-600">
          <CardHeader>
            <CardTitle className="flex items-center text-red-900">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Attention : {conflicts.length} conflit(s) détecté(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {conflicts.slice(0, 5).map((conflict, index) => (
                <div key={index} className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-red-900">{conflict.conflict_type}</p>
                      <p className="text-sm text-red-700">{conflict.description}</p>
                    </div>
                    <span className="text-xs px-2 py-1 bg-red-600 text-white rounded">
                      {conflict.severity}
                    </span>
                  </div>
                </div>
              ))}
              {conflicts.length > 5 && (
                <p className="text-sm text-gray-500 text-center">
                  +{conflicts.length - 5} autres conflits...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Vue Calendrier Améliorée
  const renderCalendar = () => (
    <Card>
      <CardContent className="p-0">
        <div className="grid grid-cols-7 divide-x">
          {getWeekDays().map((day, index) => (
            <div key={index} className="min-h-[500px]">
              <div className="p-3 border-b bg-gradient-to-r from-blue-50 to-blue-100 sticky top-0">
                <div className="font-semibold text-sm text-blue-900">
                  {format(day, 'EEEE', { locale: fr })}
                </div>
                <div className="text-3xl font-bold text-blue-900">
                  {format(day, 'd')}
                </div>
                <div className="text-xs text-blue-600">
                  {format(day, 'MMM yyyy', { locale: fr })}
                </div>
              </div>
              
              <div className="p-2 space-y-2">
                {getShiftsForDate(day).map(shift => {
                  const typeColor = shiftTypeColors[shift.shift_type] || shiftTypeColors.regular;
                  const statusColor = statusColors[shift.status] || statusColors.scheduled;
                  
                  return (
                    <div
                      key={shift.id}
                      className={`p-3 rounded-lg border-l-4 ${typeColor.border} ${typeColor.bg} cursor-pointer hover:shadow-lg transition-all duration-200 group`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-gray-900">
                            {shift.agent_name}
                          </div>
                          <div className="text-xs text-gray-600 flex items-center mt-1">
                            <Clock className="w-3 h-3 mr-1" />
                            {shift.start_time} - {shift.end_time}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteShift(shift.id)}
                          className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="text-xs text-gray-700 flex items-center mb-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        {shift.site_name}
                      </div>
                      
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${statusColor.bg} ${statusColor.text}`}>
                          {statusColor.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          {shift.duration_hours}h
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  // Vue Gantt (Simplifiée pour v1)
  const renderGantt = () => (
    <Card>
      <CardHeader>
        <CardTitle>📊 Vue Gantt - Timeline des Plages</CardTitle>
        <CardDescription>Visualisation chronologique des affectations</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {agents.slice(0, 10).map(agent => {
            const agentShifts = shifts.filter(s => s.agent_id === agent.id);
            const totalHours = agentShifts.reduce((acc, s) => acc + (s.duration_hours || 0), 0);
            
            return (
              <div key={agent.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{agent.name}</h4>
                    <p className="text-sm text-gray-600">{totalHours}h cette semaine</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{agentShifts.length} plages</p>
                  </div>
                </div>
                
                <div className="relative h-12 bg-gray-100 rounded">
                  {agentShifts.map((shift, idx) => {
                    const shiftDate = parseISO(shift.shift_date);
                    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
                    const dayIndex = Math.floor((shiftDate - weekStart) / (1000 * 60 * 60 * 24));
                    const left = (dayIndex / 7) * 100;
                    const width = (1 / 7) * 100;
                    const typeColor = shiftTypeColors[shift.shift_type] || shiftTypeColors.regular;
                    
                    return (
                      <div
                        key={idx}
                        className={`absolute h-10 top-1 ${typeColor.bg} border-2 ${typeColor.border} rounded shadow-sm flex items-center justify-center text-xs font-medium ${typeColor.text} hover:shadow-lg transition-shadow cursor-pointer`}
                        style={{ left: `${left}%`, width: `${width - 1}%` }}
                        title={`${shift.site_name} - ${shift.start_time} à ${shift.end_time}`}
                      >
                        <span className="truncate px-1">{shift.site_name?.substring(0, 10)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  // Vue Kanban
  const renderKanban = () => {
    const columns = [
      { id: 'scheduled', label: 'À venir', status: 'scheduled', color: 'blue' },
      { id: 'in_progress', label: 'En cours', status: 'in_progress', color: 'yellow' },
      { id: 'completed', label: 'Terminé', status: 'completed', color: 'green' },
      { id: 'cancelled', label: 'Annulé', status: 'cancelled', color: 'red' }
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map(column => {
          const columnShifts = shifts.filter(s => s.status === column.status);
          
          return (
            <Card key={column.id} className={`border-t-4 border-t-${column.color}-600`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>{column.label}</span>
                  <span className={`text-sm px-2 py-1 rounded bg-${column.color}-100 text-${column.color}-900`}>
                    {columnShifts.length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                {columnShifts.map(shift => {
                  const typeColor = shiftTypeColors[shift.shift_type] || shiftTypeColors.regular;
                  
                  return (
                    <div
                      key={shift.id}
                      className={`p-3 rounded-lg border ${typeColor.bg} ${typeColor.border} cursor-move hover:shadow-md transition-shadow`}
                    >
                      <div className="font-semibold text-sm text-gray-900 mb-1">
                        {shift.agent_name}
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        📅 {format(parseISO(shift.shift_date), 'dd MMM', { locale: fr })}
                      </div>
                      <div className="text-xs text-gray-700 mb-1">
                        🏢 {shift.site_name}
                      </div>
                      <div className="text-xs text-gray-600">
                        ⏰ {shift.start_time} - {shift.end_time}
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span className={`text-xs px-2 py-0.5 rounded ${typeColor.bg} ${typeColor.text} border ${typeColor.border}`}>
                          {typeColor.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          {shift.duration_hours}h
                        </span>
                      </div>
                    </div>
                  );
                })}
                {columnShifts.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <LayoutGrid className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucune plage</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header avec Gradient */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center">
              <Zap className="w-10 h-10 mr-3 animate-pulse" />
              HTS PLANNING PRO
            </h1>
            <p className="text-blue-100 text-lg">
              Système de planification professionnel pour High Tech Security
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              onClick={detectConflicts}
              variant="secondary"
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Détecter Conflits
            </Button>
            <Button
              onClick={() => setShowShiftModal(true)}
              className="bg-green-500 hover:bg-green-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Plage
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation et Filtres */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            {/* Navigation temporelle */}
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek(-1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="text-lg font-semibold min-w-[250px] text-center">
                Semaine du {format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'dd MMM', { locale: fr })} au {format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'dd MMM yyyy', { locale: fr })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek(1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Aujourd'hui
              </Button>
            </div>

            {/* Sélection des vues */}
            <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
              <Button
                variant={activeView === 'dashboard' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveView('dashboard')}
                className={activeView === 'dashboard' ? 'bg-blue-600 text-white' : ''}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button
                variant={activeView === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveView('calendar')}
                className={activeView === 'calendar' ? 'bg-blue-600 text-white' : ''}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Calendrier
              </Button>
              <Button
                variant={activeView === 'gantt' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveView('gantt')}
                className={activeView === 'gantt' ? 'bg-blue-600 text-white' : ''}
              >
                <LayoutList className="w-4 h-4 mr-2" />
                Gantt
              </Button>
              <Button
                variant={activeView === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveView('kanban')}
                className={activeView === 'kanban' ? 'bg-blue-600 text-white' : ''}
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                Kanban
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Légende des couleurs */}
      <Card className="bg-gradient-to-r from-gray-50 to-gray-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-semibold text-gray-700">Types de plages :</span>
              {Object.entries(shiftTypeColors).map(([key, color]) => (
                <div key={key} className="flex items-center">
                  <div className={`w-4 h-4 rounded ${color.bg} border-2 ${color.border} mr-2`} />
                  <span className="text-sm text-gray-600">{color.label}</span>
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-500">
              {loading && <span>🔄 Chargement...</span>}
              {!loading && <span>✅ À jour</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contenu Principal selon la vue sélectionnée */}
      <div className="min-h-[600px]">
        {activeView === 'dashboard' && renderDashboard()}
        {activeView === 'calendar' && renderCalendar()}
        {activeView === 'gantt' && renderGantt()}
        {activeView === 'kanban' && renderKanban()}
      </div>

      {/* Modal Nouvelle Plage */}
      {showShiftModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>➕ Nouvelle Plage de Travail</CardTitle>
                <button onClick={() => setShowShiftModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Agent *</Label>
                  <select
                    value={newShift.agent_id}
                    onChange={(e) => setNewShift({...newShift, agent_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner un agent</option>
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>{agent.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Site *</Label>
                  <select
                    value={newShift.site_id}
                    onChange={(e) => setNewShift({...newShift, site_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner un site</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={newShift.shift_date}
                    onChange={(e) => setNewShift({...newShift, shift_date: e.target.value})}
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Type de plage</Label>
                  <select
                    value={newShift.shift_type}
                    onChange={(e) => setNewShift({...newShift, shift_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="regular">Régulière</option>
                    <option value="overtime">Heure sup</option>
                    <option value="training">Formation</option>
                    <option value="on_call">Astreinte</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Heure début *</Label>
                  <Input
                    type="time"
                    value={newShift.start_time}
                    onChange={(e) => setNewShift({...newShift, start_time: e.target.value})}
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Heure fin *</Label>
                  <Input
                    type="time"
                    value={newShift.end_time}
                    onChange={(e) => setNewShift({...newShift, end_time: e.target.value})}
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowShiftModal(false)}>
                  Annuler
                </Button>
                <Button onClick={createShift} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  {loading ? '⏳ Création...' : '✅ Créer la Plage'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PlanningPro;
